import { keyframes } from "@emotion/react";
import styled from "@emotion/styled";
import { useEffect, useState } from "react";
import { mediaQuery } from "src/common";
import { colors } from "src/theme";
import { CloseIcon } from "../icons";

const slideUp = keyframes`
  from {
    transform: translateY(100%);
  }
  to {
    transform: translateY(0);
  }
`;

const slideDown = keyframes`
  from {
    transform: translateY(0);
  }
  to {
    transform: translateY(100%);
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const fadeOut = keyframes`
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
`;

const BottomDrawerOverlay = styled.div<{ isOpen: boolean; isClosing: boolean }>(({ isOpen, isClosing }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 1000,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  animation: isOpen && !isClosing 
    ? `${fadeIn} 0.3s ease-in-out`
    : `${fadeOut} 0.3s ease-in-out`,
  display: isOpen ? 'block' : 'none',
}));

const BottomDrawerContent = styled.div<{ isOpen: boolean; isClosing: boolean; offset?: number; backgroundColor?: string }>(({ isOpen, isClosing, offset = 0, backgroundColor = colors.white }) => ({
  position: 'fixed',
  bottom: offset,
  left: 0,
  right: 0,
  backgroundColor: backgroundColor,
  borderTopLeftRadius: 16,
  borderTopRightRadius: 16,
  padding: 24,
  maxHeight: `calc(80vh - ${offset}px)`,
  overflow: 'auto',
  zIndex: 1001,
  animation: isOpen && !isClosing 
    ? `${slideUp} 0.3s ease-in-out`
    : `${slideDown} 0.3s ease-in-out`,
  boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.15)',
  
  [mediaQuery('tablet')]: {
    padding: 32,
  }
}));

const CloseButtonContainer = styled.div<{ closeIconColor?: string }>(({ closeIconColor = colors.black }) => ({
  position: 'absolute',
  top: 16,
  right: 16,
  zIndex: 1002,
  color: closeIconColor,
}));

const Handle = styled.div({
  width: 40,
  height: 4,
  backgroundColor: colors.border6,
  borderRadius: 2,
  margin: '0 auto 16px auto',
});

export interface BottomDrawerProps {
  open: boolean;
  onClose: () => void;
  children?: React.ReactNode;
  title?: string;
  offset?: number;
  backgroundColor?: string;
  closeIconColor?: string;
}

const BottomDrawer: React.FC<BottomDrawerProps> = ({
  open,
  onClose,
  children,
  offset = 0,
  backgroundColor,
  closeIconColor = colors.black,
}) => {
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    if (open) {
      const currentOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = currentOverflow;
      };
    }
    return undefined;
  }, [open]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      onClose();
    }, 300);
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  if (!open && !isClosing) {
    return null;
  }

  return (
    <BottomDrawerOverlay isOpen={open} isClosing={isClosing} onClick={handleOverlayClick}>
      <BottomDrawerContent isOpen={open} isClosing={isClosing} offset={offset} backgroundColor={backgroundColor}>
        <Handle />
        <CloseButtonContainer>
          <CloseIcon onClick={handleClose} color={closeIconColor} />
        </CloseButtonContainer>
        {children}
      </BottomDrawerContent>
    </BottomDrawerOverlay>
  );
};

export default BottomDrawer;
